import './index.less';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { arrayToTree } from 'performant-array-to-tree';
import Highlighter from 'react-highlight-words';

import {
  INDUSTRY_AVG_ID,
  isIndustryAvgOrg,
  isPlatformOrg,
  PLATFORM_ORG_ID,
} from 'domains/orgDomain';
import { Tree, Space } from 'antd';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { DataNode, EventDataNode } from 'antd/lib/tree';
import { Key } from 'antd/lib/table/interface';
import { useOrg } from 'hooks/useOrg';
import classNames from 'classnames';
import { keyBy, uniq, uniqBy } from 'lodash-es';
import { Organization } from 'domains/orgDomain';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';

const getPlatformItem = (orgs: Organization[]) => {
  return orgs.find((item) => item.id === PLATFORM_ORG_ID);
};

const getIndustryAvgItem = (orgs: Organization[]) => {
  return orgs.find((item) => item.id === INDUSTRY_AVG_ID);
};

const getRegularOrgs = (orgs: Organization[]) => {
  return orgs.filter((item) => item.id !== PLATFORM_ORG_ID);
};

type Props = {
  searchValue?: string;
};
const OrgSwitch = ({ searchValue = '' }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const orgs = useAppSelector((state) => state.org.list);
  const org = useOrg();
  const loading = useAppSelector((state) => state.loading.effects.org.getOrgs);

  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);
  const [searchExpandedKeys, setSearchExpandedKeys] = useState<Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);

  const platform = getPlatformItem(orgs);
  const industryAvg = getIndustryAvgItem(orgs);

  useEffect(() => {
    if (org) {
      setSelectedKeys([org.id]);
    }
  }, [org]);

  const treeData = useMemo(() => {
    const orgKeyMap = keyBy(orgs, 'id');
    const items: Organization[] = [];
    const newExpandedKeys: number[] = [];
    getRegularOrgs(orgs)
      .filter((item) => item.id !== INDUSTRY_AVG_ID)
      .forEach((item) => {
        const isSearchHit =
          !searchValue ||
          String(item.id) === searchValue ||
          item.name.toLowerCase().includes(searchValue.toLowerCase());

        if (isSearchHit) {
          if (searchValue || item.id === org?.id) {
            // put parent nodes
            let parent = orgKeyMap[item.parentId];
            while (parent) {
              newExpandedKeys.push(parent.id);
              items.push(parent);
              parent = orgKeyMap[parent.parentId];
            }
          }

          items.push(item);
        }
      });
    setSearchExpandedKeys(newExpandedKeys);

    const arr: DataNode[] = uniqBy(items, 'id').map((item) => ({
      title: (
        <RevxFlexLayout className="org-item org-tree-item">
          <Space>
            <span className="org-item-id">{item.id}</span>
            <span>-</span>
            <span className="org-item-name">
              <Highlighter
                highlightClassName="revx-highlighter"
                searchWords={[searchValue]}
                autoEscape={true}
                textToHighlight={item.name}
              />
            </span>
          </Space>
        </RevxFlexLayout>
      ),
      key: item.id,
      parentId: item.parentId,
    }));
    return arrayToTree(arr, {
      id: 'key',
      parentId: 'parentId',
      dataField: null,
    });
  }, [org?.id, orgs, searchValue]);

  const onTreeExpanded = (expandedKeys: Key[]) => {
    setSearchExpandedKeys([]);
    setExpandedKeys(expandedKeys);
  };

  const onTreeSelect = (
    selectedKeys: Key[],
    { selected, node }: { selected: boolean; node: EventDataNode }
  ) => {
    if (selected) {
      setSelectedKeys(selectedKeys);
      dispatch.app.switchOrg(node.key);
      setExpandedKeys(expandedKeys.concat(selectedKeys));
    } else {
      setExpandedKeys(expandedKeys.filter((key) => key !== node.key));
    }
  };

  return (
    <RevxSpin spinning={loading}>
      {platform && (
        <section className="platform-con">
          <RevxFlexLayout className="org-title">
            {t('platform.sider.platformTitle')}
          </RevxFlexLayout>
          <div className="org-list">
            <RevxButton
              type="text"
              className={classNames('org-item', {
                active: !org || isPlatformOrg(org),
              })}
              onClick={() => {
                dispatch.app.switchOrg(platform.id);
              }}
            >
              <RevxFlexLayout>
                <Space>
                  <span className="org-item-id">{platform.id}</span>
                  <span>-</span>
                  <span className="org-item-name">{platform.name}</span>
                </Space>
              </RevxFlexLayout>
            </RevxButton>
          </div>
        </section>
      )}

      {industryAvg && (
        <section className="platform-con">
          <RevxFlexLayout className="org-title">
            {t('platform.sider.industryAvgTitle')}
          </RevxFlexLayout>
          <div className="org-list">
            <RevxButton
              type="text"
              className={classNames('org-item', {
                active: org && isIndustryAvgOrg(org),
              })}
              onClick={() => {
                dispatch.app.switchOrg(industryAvg.id);
              }}
            >
              <RevxFlexLayout>
                <Space>
                  <span className="org-item-id">{industryAvg.id}</span>
                  <span>-</span>
                  <span className="org-item-name">{industryAvg.name}</span>
                </Space>
              </RevxFlexLayout>
            </RevxButton>
          </div>
        </section>
      )}
      <section className="orgs-con">
        <RevxFlexLayout className="org-title">
          {t('platform.sider.orgTitle')}
        </RevxFlexLayout>
        <Tree
          treeData={treeData as DataNode[]}
          selectedKeys={selectedKeys}
          expandedKeys={uniq(expandedKeys.concat(searchExpandedKeys))}
          onSelect={onTreeSelect}
          onExpand={onTreeExpanded}
        />
      </section>
    </RevxSpin>
  );
};

export default OrgSwitch;
